import { z } from 'zod';
import { IsoDateTime, PageQuery, Uuid, page, withDefaults } from './common.js';
/**
 * Module `integrations` (docs/modules/integrations.md, ADR 0028 Entra, ADR 0033 Slack/Teams). Owners: S47 (connections,
 * Slack and Teams channels), S47b (Entra directory sync). Microsoft sign-in (SSO) is core: schemas/core-p4.ts.
 */
export const ConnectionKind = z.enum(['slack', 'teams']);
export const ConnectionStatus = z.enum(['active', 'failing', 'disabled']);
export const Connection = z.object({
    id: Uuid,
    kind: ConnectionKind,
    name: z.string(),
    status: ConnectionStatus,
    /** Masked target, e.g. "hooks.slack.com/…/x7Qa". The URL itself is write-only (encrypted at rest). */
    targetMasked: z.string(),
    lastUsedAt: IsoDateTime.nullable(),
    lastErrorKey: z.string().nullable(),
    consecutiveFailures: z.number().int(),
    createdAt: IsoDateTime,
});
/**
 * Slack: an incoming-webhook URL (https://hooks.slack.com/services/...). Teams: a Workflows (Power Automate) webhook URL
 * for "post to a channel when a webhook request is received". Hosts are allow-listed in ADR 0033.
 */
export const ConnectionCreate = z.object({
    kind: ConnectionKind,
    name: z.string().trim().min(1).max(80),
    url: z.url().max(2000),
});
export const ConnectionUpdate = z.object({
    name: z.string().trim().min(1).max(80).optional(),
    url: z.url().max(2000).optional(),
    status: z.enum(['active', 'disabled']).optional(),
});
export const ConnectionTestResult = z.object({ ok: z.boolean(), statusCode: z.number().int().nullable(), errorKey: z.string().nullable(), durationMs: z.number().int() });
// --- Entra ID directory sync (S47b) -------------------------------------------------------------------------------------------
export const EntraStatus = z.object({
    /** False when the platform has no Entra app configured (core settings `entra.clientId`). */
    available: z.boolean(),
    connected: z.boolean(),
    directoryId: Uuid.nullable(),
    connectedAt: IsoDateTime.nullable(),
    connectedByLabel: z.string().nullable(),
    lastRunAt: IsoDateTime.nullable(),
    lastRunStatus: z.enum(['ok', 'partial', 'failed', 'stopped']).nullable(),
});
/** POST /integrations/entra/connect -> admin-consent URL; Microsoft returns to /api/v1/integrations/entra/callback. */
export const EntraConnectStart = z.object({ url: z.string() });
export const EntraGroupSearchQuery = z.object({ q: z.string().trim().min(1).max(100) });
export const EntraGroup = z.object({ id: z.string(), displayName: z.string(), memberCount: z.number().int().nullable() });
export const EntraGroupSearchResult = z.object({ items: z.array(EntraGroup) });
/** Which Entra attribute fills the member's phone. */
export const EntraPhoneSource = z.enum(['mobilePhone', 'businessPhones', 'none']);
export const EntraMapping = z.object({
    id: Uuid,
    entraGroupId: z.string(),
    entraGroupName: z.string(),
    /** A CloudSign repeat group. One Entra group maps to one CloudSign group; a person in two mapped groups goes to the first by order. */
    groupId: Uuid,
    order: z.number().int(),
    phoneSource: EntraPhoneSource,
    /** Members who leave the Entra group are archived (never deleted). */
    archiveMissing: z.boolean(),
    enabled: z.boolean(),
});
export const EntraMappingCreate = z.object({
    entraGroupId: z.string().trim().min(1).max(100),
    entraGroupName: z.string().trim().min(1).max(200),
    groupId: Uuid,
    phoneSource: EntraPhoneSource.default('mobilePhone'),
    archiveMissing: z.boolean().default(true),
});
export const EntraMappingUpdate = z.object({
    groupId: Uuid.optional(),
    order: z.number().int().min(0).max(1000).optional(),
    phoneSource: EntraPhoneSource.optional(),
    archiveMissing: z.boolean().optional(),
    enabled: z.boolean().optional(),
});
export const EntraRun = z.object({
    id: Uuid,
    trigger: z.enum(['schedule', 'manual', 'preview']),
    status: z.enum(['running', 'ok', 'partial', 'failed', 'stopped']),
    startedAt: IsoDateTime,
    finishedAt: IsoDateTime.nullable(),
    created: z.number().int(),
    updated: z.number().int(),
    archived: z.number().int(),
    restored: z.number().int(),
    skipped: z.number().int(),
    /** i18n keys with counts, e.g. { key: 'entra.skip.no_email', count: 3 }. No names. */
    problems: z.array(z.object({ key: z.string(), count: z.number().int() })),
});
export const EntraRunList = page(EntraRun);
export const EntraRunQuery = PageQuery;
/** POST /integrations/entra/sync: run now, or `preview` (counts only, nothing changed). */
export const EntraSyncRequest = z.object({ preview: z.boolean().default(false) });
// --- Settings -----------------------------------------------------------------------------------------------------------------
export const IntegrationsTenantSettings = z.object({
    entra: withDefaults(z.object({
        /** Hours between automatic syncs. 0 = only when run by hand. */
        syncEveryHours: z.number().int().min(0).max(168).default(6),
        /** Safety stop: a run that would archive more than this % of synced members stops and alerts instead. 0 = no stop. */
        maxArchivePercent: z.number().int().min(0).max(100).default(20),
        /** Email when a sync fails or stops. Empty = the tenant's administrators. */
        alertEmails: z.array(z.email()).max(5).default([]),
    })),
    /** Slack/Teams: disable a connection after this many consecutive failures (and email admins). 0 = never. */
    disableAfterFailures: z.number().int().min(0).max(100).default(20),
});
//# sourceMappingURL=integrations.js.map